import { IEditorMetadata, IHistoryLogDepricated, ITag, taskOrIssueENUM } from "@mctypes/global.types";
import { IUpdateIssueResp } from "@mctypes/tempTypes";
import { IAllFilter, IHistoryLog } from "@schemas/commonSchema";
import { IIssueApi, IIssueGis, INoteboardNode } from "@schemas/issueSchema";
import {
  GetProjectByProjectIdIssuesOnBoardApiArg,
  openapi,
} from "../../../../openapi";
import { apiSlice } from "../../../apiSlice";
import {
  removeAppModalInRedux,
  setShowFailedMessageThunk,
  setShowSuccessMessageThunk,
} from "../../../global/globalSlice";
import { ToastMessagesEnums } from "../../../mc-shared/sharedConstants";
import { trpcSlice } from "../../../trpcSlice";
import { normalizeFilters, setNestedAttr } from "../../utils";
import { updateIssueAfterResp } from "./issueapi.enhance";
import { addMissingKeysIssue, convertHistoryIssueVal } from "./issueUtils";

interface IIssueBoardInput {
  projectId: string;
  boardId: string;
}

interface IIssueQueryInput {
  projectId: string;
  issueId: string;
}

interface IIssueChangeBoardQueryInput extends IIssueQueryInput {
  boardIdToChangeTo: string;
}

interface ICopyIssueInput extends IIssueQueryInput {
  includeTasks: boolean;
}

interface IIssueAndTasksQueryInput extends IIssueQueryInput {
  boardIdToExclude: string;
}

export interface IIssueGetAllQueryInput {
  projectId: string;
  issueBoardId: string;
  allFilters: IAllFilter[];
  freeTextSearch: string;
  pns: string;
}

export interface IPostIssue {
  projectId: string;
  issueBoardId: string;
  issueTitle: string;
  taskOrIssue: taskOrIssueENUM;
  issueIdIfItIsATask?: string;
  tags?: string[];
  connectedMeeting?: string;
  clientAccess?: boolean;
  gis?: IIssueGis;
  gisImage?: string;
  connectedChecklistItem?: string;
  connectedMeetingAgendaItem?: string;
  noteboardNode?: INoteboardNode;
}

export interface IUpdateIssue {
  projectId: string;
  issueBoardId: any;
  issueId: string;
  attr: string;
  value: any;
  oldValue: any;
  gisImage?: string;
  order?: number;
}

interface IQueryPostComment {
  projectId: string;
  issueId: string;
  text: string;
  metadata: IEditorMetadata;
}

interface IQueryDeleteComment {
  projectId: string;
  issueId: string;
  commentId: string;
}

interface IQueryUpdateComment {
  projectId: string;
  issueId: string;
  text: string;
  metadata: IEditorMetadata;
  commentId: string;
}

interface IProjectIssueId {
  projectId: string;
  issueId: string;
}

interface IQueryIssueTodoAdd extends IProjectIssueId {
  text: string;
}

interface IQueryIssueTodoToggle extends IProjectIssueId {
  todoId: string;
  isChecked: boolean;
}

interface IQueryIssueTodoUpdate extends IProjectIssueId {
  todoId: string;
  text: string;
}

interface IQueryIssueTodoDelete extends IProjectIssueId {
  todoId: string;
}

const filtersQueryKey = (filters: IAllFilter[] | null | undefined) => {
  const norm = normalizeFilters(filters);
  const compact = norm.map((f) => {
    if ("options" in f && Array.isArray(f.options)) {
      return {
        df: f.dataField,
        act: f.options
          .filter((o) => o.isActive)
          .map((o) => String(o.value))
          .sort(),
      };
    }
    if ("bool" in f) return { df: f.dataField, bool: !!f.bool };
    return { df: f.dataField };
  });
  return JSON.stringify(compact);
};

export const issueRTK = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // doGetAllIssues: builder.query<IIssueApi[], IIssueGetAllQueryInput>({
    //   query: (input) => ({
    //     url: `/project/${input.projectId}/issue/board/${input.issueBoardId}`,
    //     method: "POST",
    //     body: {
    //       allFilters: input.allFilters,
    //       freeTextSearch: input.freeTextSearch,
    //       pns: input.pns,
    //     },
    //   }),
    //   transformResponse: (resp: IIssueApi[]) => {
    //     return resp.map((i) => addMissingKeysIssue(i, ""));
    //   },
    //   providesTags: ["AllIssues"],
    // }),

    doGetSingleIssue: builder.query<IIssueApi, IIssueQueryInput>({
      query: (input) => `project/${input.projectId}/issue/item/${input.issueId}`,
      providesTags: ["Issue"],
    }),

    doUpdateIssue: builder.mutation<IUpdateIssueResp, IUpdateIssue>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issue/board/${input.issueBoardId}/item/${input.issueId}`,
          method: "PUT",
          body: {
            attr: input.attr,
            value: input.value,
            oldValue: input.oldValue,
            gisImage: input.gisImage,
            order: input.order,
          },
        };
      },
      async onQueryStarted(args, api) {
        const { dispatch, queryFulfilled, getState } = api;
        await patchAllIssuesAfterSingleIssueUpdate(args, {
          dispatch,
          queryFulfilled,
          getState,
        });
      },
      invalidatesTags: ["Issue", "Meeting", "IssueFormulas", "IssueLogs"],
    }),
    createIssue: builder.mutation<IIssueApi, IPostIssue>({
      query(postIssue) {
        return {
          url: `/project/${postIssue.projectId}/issue/board/${postIssue.issueBoardId}/type/${postIssue.taskOrIssue}`,
          method: "POST",
          body: {
            title: postIssue.issueTitle,
            issueId: postIssue.issueIdIfItIsATask,
            tags: postIssue.tags,
            connectedMeeting: postIssue.connectedMeeting,
            clientAccess: postIssue.clientAccess,
            gis: postIssue.gis,
            gisImage: postIssue.gisImage,
            connectedChecklistItem: postIssue.connectedChecklistItem,
            connectedMeetingAgendaItem: postIssue.connectedMeetingAgendaItem,
            noteboardNode: postIssue.noteboardNode,
          },
        };
      },
      async onQueryStarted({ issueTitle, ...createIssueParams }, { dispatch, queryFulfilled, getState }) {
        try {
          const resp = await queryFulfilled;
          const createdIssue = addMissingKeysIssue(resp.data, "");
          const projectId = createIssueParams.projectId;
          const issueBoardId = createIssueParams.issueBoardId;
          const argsList = getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, issueBoardId);

          for (const args of argsList) {
            dispatch(
              openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft) => {
                if (createIssueParams.taskOrIssue === "ISSUE") {
                  draft.push(createdIssue as any);
                } else if (createIssueParams.taskOrIssue === "TASK") {
                  const parent = draft.find((i) => i._id === createIssueParams.issueIdIfItIsATask);
                  if (parent) {
                    parent.connectedTasks ??= [];
                    // Only add if not already present
                    if (!parent.connectedTasks.some((t) => t._id === createdIssue._id)) {
                      parent.connectedTasks.push(createdIssue as any);
                    }
                  } else {
                    // Fallback: if parent not found, add as top-level to prevent missing
                    if (!draft.some((i) => i._id === createdIssue._id)) {
                      draft.push(createdIssue as any);
                    }
                  }
                }
              }),
            );
          }

          dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.ISSUE_ADDED));
          dispatch(openapi.util.invalidateTags(["ConnectedTasks"]));
        } catch (err) {
          console.warn("createIssue.onQueryStarted handled error:", err);
        }
      },
      invalidatesTags: ["Issue", "Meeting", "IssueFormulas"],
    }),
    connectIssueOrTaskFromOtherBoardsToIssue: builder.mutation<
      IUpdateIssueResp,
      { projectId: string; issueOrTaskId: string; issueId: string; connectOrDisconnect: "CONNECT" | "DISCONNECT" }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/issue/task/connect-disconnect-issue-or-task-from-other-boards`,
          method: "POST",
          body: {
            issueOrTaskId: input.issueOrTaskId,
            issueId: input.issueId,
            connectOrDisconnect: input.connectOrDisconnect,
          },
        };
      },
      async onQueryStarted(args, api) {
        await patchAllIssuesAfterSingleIssueUpdate(args, api);
      },
      invalidatesTags: ["Issue"],
    }),
    getUniqueTagsOnBoard: builder.query<ITag[], IIssueBoardInput>({
      query: (input) => `/project/${input.projectId}/issue/board/${input.boardId}/tags`,
      providesTags: ["SingleIssueBoard", "Tags"],
    }),

    archiveIssue: builder.mutation<IIssueApi, IIssueQueryInput>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issue/item/${input.issueId}/archive`,
          method: "PUT",
        };
      },
      async onQueryStarted({ issueId }, { dispatch, queryFulfilled, getState }) {
        try {
          const resp = await queryFulfilled;
          const taskOrIssue = resp.data.taskOrIssue;
          const queries = (getState() as any).api?.queries;
          const projectId = resp.data.project;
          const issueBoardId = resp.data.issueBoard._id;
          const argsList = getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, issueBoardId);

          for (const args of argsList) {
            dispatch(
              openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft) => {
                const index = draft.findIndex((issue) => issue._id === issueId);
                if (index >= 0) draft.splice(index, 1);
              }),
            );
            if (taskOrIssue === "TASK") {
              dispatch(
                openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft) => {
                  draft.map((issue) => {
                    if (issue._id === resp.data.connectedIssue?._id) {
                      const idx = (issue.connectedTasks as IIssueApi[]).findIndex((t) => t._id === issueId);
                      if (idx >= 0) (issue.connectedTasks as IIssueApi[]).splice(idx, 1);
                    }
                    return issue;
                  });
                }),
              );
            }
          }
          dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.ISSUE_ARCHIVED));
          dispatch(openapi.util.invalidateTags(["ConnectedTasks"]));
        } catch (err) {
          dispatch(setShowFailedMessageThunk("Kunne ikke arkivere saken"));
          console.warn("archiveIssue.onQueryStarted handled error:", err);
        }
      },
      invalidatesTags: ["Issue", "Meeting"],
    }),

    addIssueComment: builder.mutation<IIssueApi, IQueryPostComment>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issue/item/${input.issueId}/comment`,
          method: "POST",
          body: { text: input.text, metadata: input.metadata },
        };
      },
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState as any, dispatch);
      },
      invalidatesTags: ["Issue"],
    }),
    deleteIssueComment: builder.mutation<IIssueApi, IQueryDeleteComment>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issue/item/${input.issueId}/comment/${input.commentId}`,
          method: "DELETE",
        };
      },
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState as any, dispatch);
      },
      invalidatesTags: ["Issue"],
    }),

    updateIssueComment: builder.mutation<IIssueApi, IQueryUpdateComment>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issue/item/${input.issueId}/comment/${input.commentId}`,
          method: "PUT",
          body: { text: input.text, metadata: input.metadata },
        };
      },
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState as any, dispatch);
      },
      invalidatesTags: ["Issue"],
    }),
    addIssueTodo: builder.mutation<IUpdateIssueResp, IQueryIssueTodoAdd>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issue/todo/${input.issueId}/add`,
          method: "POST",
          body: { text: input.text },
        };
      },
      async onQueryStarted(args, api) {
        await patchAllIssuesAfterSingleIssueUpdate(args, api);
      },
      invalidatesTags: ["Issue"],
    }),

    updateIssueTodo: builder.mutation<IUpdateIssueResp, IQueryIssueTodoUpdate>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issue/todo/${input.issueId}/update`,
          method: "PUT",
          body: { text: input.text, todoId: input.todoId },
        };
      },
      async onQueryStarted(args, api) {
        await patchAllIssuesAfterSingleIssueUpdate(args, api);
      },
      invalidatesTags: ["Issue"],
    }),

    deleteIssueTodo: builder.mutation<IUpdateIssueResp, IQueryIssueTodoDelete>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issue/todo/${input.issueId}/delete/${input.todoId}`,
          method: "DELETE",
        };
      },
      async onQueryStarted(args, api) {
        await patchAllIssuesAfterSingleIssueUpdate(args, api);
      },
      invalidatesTags: ["Issue"],
    }),

    toggleIssueTodo: builder.mutation<IUpdateIssueResp, IQueryIssueTodoToggle>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issue/todo/${input.issueId}/toggle`,
          method: "PUT",
          body: { isChecked: input.isChecked, todoId: input.todoId },
        };
      },
      async onQueryStarted(args, api) {
        await patchAllIssuesAfterSingleIssueUpdate(args, api);
      },
      invalidatesTags: ["Issue"],
    }),

    toggleClientAccess: builder.mutation<void, IIssueQueryInput>({
      query(input) {
        return { url: `/project/${input.projectId}/issue/item/${input.issueId}/clientaccess`, method: "PUT" };
      },
      invalidatesTags: ["Issue"],
    }),

    toggleSubscription: builder.mutation<void, IIssueQueryInput>({
      query(input) {
        return { url: `/project/${input.projectId}/issue/item/${input.issueId}/subscribe`, method: "PUT" };
      },
      invalidatesTags: ["Issue"],
    }),

    getIssueHistoryLogs: builder.query<IHistoryLog[], IIssueQueryInput>({
      query: (input) => `/project/${input.projectId}/issue/log/item/${input.issueId}`,
      transformResponse: (resp: IHistoryLogDepricated[]) =>
        resp.map((issue) => ({
          _id: issue._id,
          dataField: issue.column,
          columnDisplayName: issue.column,
          createdAt: issue.createdAt,
          newValue: convertHistoryIssueVal(issue.newValue),
          oldValue: convertHistoryIssueVal(issue.oldValue),
          updatedAt: issue.updatedAt,
          editedBy: issue?.user?.name ?? issue.userName,
        })),
      providesTags: ["IssueLogs"],
    }),

    changeBoardOfIssueItem: builder.mutation<IIssueApi, IIssueChangeBoardQueryInput>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issue/item/${input.issueId}/board`,
          method: "PUT",
          body: { boardIdToChangeTo: input.boardIdToChangeTo },
        };
      },
      async onQueryStarted({ issueId }, { dispatch, queryFulfilled, getState }) {
        try {
          const resp = await queryFulfilled;
          const projectId = resp.data.project;
          const issueBoardId = resp.data.issueBoard as string;
          const argsList = getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, issueBoardId);

          for (const args of argsList) {
            dispatch(
              openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft) => {
                const index = draft.findIndex((issue) => issue._id === issueId);
                if (index >= 0) draft.splice(index, 1);
              }),
            );
          }

          dispatch(removeAppModalInRedux(issueId));
          dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
        } catch (err) {
          console.warn("changeBoardOfIssueItem.onQueryStarted handled error:", err);
        }
      },
      invalidatesTags: ["Issue"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useDoGetSingleIssueQuery,
  useCreateIssueMutation,
  useDoUpdateIssueMutation,
  useConnectIssueOrTaskFromOtherBoardsToIssueMutation,
  useGetUniqueTagsOnBoardQuery,
  useArchiveIssueMutation,
  useAddIssueCommentMutation,
  useDeleteIssueCommentMutation,
  useUpdateIssueCommentMutation,
  useAddIssueTodoMutation,
  useUpdateIssueTodoMutation,
  useDeleteIssueTodoMutation,
  useToggleIssueTodoMutation,
  useToggleClientAccessMutation,
  useToggleSubscriptionMutation,
  useGetIssueHistoryLogsQuery,
  useChangeBoardOfIssueItemMutation,
} = issueRTK;

export const patchAllIssuesAfterSingleIssueUpdate = async (
  mutationArgs: {
    projectId: string;
    issueId: string;
    attr?: string;
    value?: any;
  },
  {
    dispatch,
    queryFulfilled,
    getState,
  }: { dispatch: any; queryFulfilled: Promise<{ data: IUpdateIssueResp }>; getState: any },
) => {
  const { issueId, projectId, attr, value } = mutationArgs;

  // ===== 1) Optimistisk patch av doGetSingleIssue =====
  let singleIssuePatch: { undo: () => void } | undefined;

  if (projectId && issueId && attr != null && value != null) {
    const singleIssueArgs: IIssueQueryInput = { projectId, issueId };

    singleIssuePatch = dispatch(
      issueRTK.util.updateQueryData("doGetSingleIssue", singleIssueArgs, (draft: IIssueApi) => {
        if (!draft) return;
        const updated = setNestedAttr(draft, attr, value);
        Object.assign(draft, updated); // skriv tillbaka i Immer-draften
      }),
    );
  }

  let resp: { data: IUpdateIssueResp };

  try {
    resp = await queryFulfilled;
  } catch (err) {
    if (singleIssuePatch) {
      singleIssuePatch.undo();
    }
    dispatch(setShowFailedMessageThunk("Kunne ikke lagre endringer"));
    console.warn("patchAllIssuesAfterSingleIssueUpdate: query rejected", err);
    return;
  }

  // ev. följdeffekter
  dispatch(openapi.util.invalidateTags(["TrpcMembersByUserRelation"]));
  dispatch(openapi.util.invalidateTags(["ConnectedIssues"]));
  dispatch(openapi.util.invalidateTags(["ConnectedTasks"]));

  const data = resp.data;
  const updatedIssue = data?.updatedIssue;

  if (!updatedIssue) {
    console.warn("No updatedIssue in response");
    return;
  }

  const issueBoardId = updatedIssue.issueBoard._id;

  const argsList = getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, issueBoardId);

  for (const args of argsList) {
    dispatch(
      openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
        for (let i = 0; i < draft.length; i++) {
          if (draft[i]._id === issueId) {
            draft[i] = updatedIssue as any; // ersätt hela objektet
            break;
          }
          try {
            if (draft[i].connectedTasksAndIssuesFromOtherBoards?.length > 0) {
              for (let j = 0; j < draft[i].connectedTasksAndIssuesFromOtherBoards!.length; j++) {
                if ((draft[i].connectedTasksAndIssuesFromOtherBoards as any)[j]._id === issueId) {
                  (draft[i].connectedTasksAndIssuesFromOtherBoards as any)[j] = updatedIssue;
                  break;
                }
              }
            }
          } catch (err) {
            console.warn("Error updating issue in cache:", err);
          }
        }
      }),
    );
  }

  // 2) Om det är en TASK: uppdatera nested task i parent-issue.connectedTasks
  if (updatedIssue.taskOrIssue === "TASK") {
    const connectedIssueId = updatedIssue.connectedIssue?._id;
    if (connectedIssueId) {
      for (const args of argsList) {
        dispatch(
          openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
            for (const issue of draft) {
              if (issue._id !== connectedIssueId) continue;
              if (!issue.connectedTasks) break;

              for (let t = 0; t < issue.connectedTasks.length; t++) {
                if ((issue.connectedTasks as any)[t]._id === issueId) {
                  (issue.connectedTasks as any)[t] = updatedIssue as any; // ersätt hela task-objektet
                  return; // klart för denna lista
                }
              }
              return;
            }
          }),
        );
      }
    }
  }

  // 3) Uppdatera kanbanOrder för ev. andra issues som kom i svaret
  if (Array.isArray(resp.data.issuesWithKanbanOrder) && resp.data.issuesWithKanbanOrder.length > 0) {
    const byId = new Map(resp.data.issuesWithKanbanOrder.map((x) => [x._id, x.kanbanOrder]));
    for (const args of argsList) {
      const patchResult = dispatch(
        openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
          for (const issue of draft) {
            const ko = byId.get(issue._id);
            if (ko !== undefined) issue.kanbanOrder = ko as any;
          }
        }),
      );
      if (patchResult.patches.length === 0) {
        console.warn("No kanban patches were made to the cache");
      }
    }
  }
};

const enhancedApi = openapi.enhanceEndpoints({
  endpoints: {
    putProjectByProjectIdIssuesUpdateGantActivity: {
      invalidatesTags: ["Issue"],
      async onQueryStarted(resp, { dispatch, queryFulfilled, getState }) {
        await patchAllIssuesAfterSingleIssueUpdate(
          {
            projectId: resp.projectId,
            issueId: resp.body.issueId,
            attr: "gantActivities",
            value: resp.body.gantActivities,
          },
          { dispatch, queryFulfilled, getState },
          //   "gantActivities",
        );
      },
    },
    putProjectByProjectIdIssuesKanbanreorder: {
      async onQueryStarted(resp, { dispatch, queryFulfilled, getState }) {
        dispatch(issueRTK.util.invalidateTags(["Issue"]));
        await patchAllIssuesAfterSingleIssueUpdate(
          { projectId: resp.projectId, issueId: resp.body.issueId, attr: "kanbanOrder" },
          { dispatch, queryFulfilled, getState },
          //  "kanbanOrder",
        );
      },
    },
  },
});

export type ConnectAction = "CONNECT" | "DISCONNECT";
export type UndoFn = () => void;

/** Plocka args för doGetAllIssues från RTK Query-cachen.
 *  - Fungerar oavsett RootState-typ
 *  - Fungerar oavsett reducerPath (t.ex. "api", "openapi"…)
 */
export function getDoGetAllIssuesArgs(
  stateOrGetState: unknown | (() => unknown),
  api: { reducerPath: string },
  //predicate?: (args: GetProjectByProjectIdIssuesOnBoardApiArg) => boolean,
): GetProjectByProjectIdIssuesOnBoardApiArg[] {
  const state = typeof stateOrGetState === "function" ? (stateOrGetState as () => unknown)() : stateOrGetState;
  const queries = (state as any)?.[api.reducerPath]?.queries ?? {};
  const out: GetProjectByProjectIdIssuesOnBoardApiArg[] = [];

  for (const key of Object.keys(queries)) {
    if (!key.startsWith("getProjectByProjectIdIssuesOnBoard(")) continue;
    const open = key.indexOf("(");
    if (open === -1 || !key.endsWith(")")) continue;

    try {
      const json = key.slice(open + 1, -1);
      const args = JSON.parse(json) as GetProjectByProjectIdIssuesOnBoardApiArg;
      out.push(args);
    } catch {
      // ignore parse error
    }
  }
  return out;
}

/** Hjälper att filtrera på ett specifikt board. */
export function getDoGetAllIssuesArgsForBoard(
  stateOrGetState: unknown | (() => unknown),
  api: { reducerPath: string },
  projectId: string,
  issueBoardId: string,
): GetProjectByProjectIdIssuesOnBoardApiArg[] {
  return getDoGetAllIssuesArgs(stateOrGetState, api);
}
